/**
 * Sync engine.
 *
 * Opportunistic, idempotent and resumable — it assumes the network is the
 * exception rather than the rule. Nothing in the UI ever awaits this; screens
 * read IndexedDB and this reconciles in the background when it can.
 *
 * One round trip carries the delta both ways (`transport.sync`), then media
 * follows one file per request, so a connection dropping in a hotel lobby costs
 * one file rather than the whole batch.
 *
 * The transport is injected. Phase 2 ships only `offlineTransport`; phase 3
 * supplies the real client over /fairTracker/*. That seam is what lets the
 * queue be drained against a stub in tests.
 */

import { getAll, writeMerged, readMeta, writeMeta, getSupplier, saveSupplier } from './repo';
import { currentIdentity } from './db';
import { META } from './schema';
import * as outbox from './outbox';
import * as media from './media';
import { mergeList } from './merge';
import { normalizeFair, normalizeSupplier } from './entities';

/** A transport that is always offline. Replaced in phase 3. */
export const offlineTransport = {
  isOnline: () => false,
  sync: () => Promise.reject(new Error('offline')),
  uploadMedia: () => Promise.reject(new Error('offline')),
};

let transport = offlineTransport;

export function setTransport(t) {
  transport = t || offlineTransport;
}

/** So a caller that swaps the transport temporarily can put it back. */
export const getTransport = () => transport;

export const online = () =>
  typeof navigator === 'undefined' || navigator.onLine !== false
    ? transport.isOnline()
    : false;

/* ── State, so the UI can show a sync indicator ─────────────────────────── */

let state = { status: 'idle', pending: 0, lastSyncAt: null, error: null };
const listeners = new Set();

export const syncState = () => state;
export function onSyncChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (e) {
      /* a listener must not break the engine */
    }
  });
}

/**
 * Forget the previous partition's status.
 *
 * This state is module-level and so outlives an account switch, which would
 * leave Settings showing one buyer's "Last synced <time>" and pending count
 * under another's account — a small leak, but a leak, and misleading besides.
 */
export function resetState() {
  setState({ status: 'idle', pending: 0, lastSyncAt: null, error: null });
}

export async function refreshPendingCount() {
  const [queued, files] = await Promise.all([outbox.pendingCount(), media.pendingUploads()]);
  setState({ pending: queued + files.length });
  return state.pending;
}

/* ── The run ────────────────────────────────────────────────────────────── */

const sameIdentity = (a, b) => !!a && !!b && a.userId === b.userId && a.spaceId === b.spaceId;

let running = null;

/**
 * Pull the server's delta, merge it, push ours, then upload media.
 *
 * Safe to call freely — concurrent calls share the in-flight run, and the whole
 * thing is a no-op when offline. Resolves to a result object; it never throws,
 * because every caller is a background trigger that must not surface an error
 * on the capture path.
 */
export function syncNow(opts = {}) {
  if (running) return running;
  running = run(opts).finally(() => {
    running = null;
  });
  return running;
}

async function run({ full = false } = {}) {
  if (!online()) {
    setState({ status: 'offline' });
    await refreshPendingCount();
    return { skipped: 'offline' };
  }

  /* Which account this run belongs to.

     Partitioning the store (schema.js) already makes it impossible to READ
     another account's queue, but a sign-out or a workspace switch can still
     land in the middle of a run that is already holding rows in memory. Two
     outcomes would be unacceptable, and both are guarded below: pushing one
     buyer's queued suppliers under another's token — the server stamps them as
     the second buyer's, which is how this leaked in the first place — and
     merging one account's server response into another's store. */
  const startedAs = currentIdentity();
  if (!startedAs) {
    setState({ status: 'idle' });
    return { skipped: 'no-identity' };
  }

  /** Walk away without settling, so the queue stays intact for its own partition. */
  const abandon = () => {
    setState({ status: 'idle' });
    return { skipped: 'identity-changed' };
  };

  setState({ status: 'syncing', error: null });

  try {
    const rows = await outbox.pending();
    const { send, drop } = outbox.coalesce(rows);
    const since = full ? 0 : (await readMeta(META.LAST_SYNC_AT)) || 0;

    if (!sameIdentity(startedAs, currentIdentity())) return abandon();

    const res = await transport.sync({
      since,
      changes: send.map((r) => ({ entity: r.entity, id: r.id, op: r.op, payload: r.payload })),
    });

    // The round trip is the slowest step here and so the likeliest place for a
    // switch to land. Everything past this point WRITES — the merge into the
    // local store, the settle, the sync clock — and all of it would land in
    // whichever partition is open now rather than the one this run read from.
    if (!sameIdentity(startedAs, currentIdentity())) return abandon();

    // Merge what came back BEFORE settling the queue: if writing the merge
    // fails we would rather re-send than lose a pending change.
    const local = await getAll();
    const fairs = mergeList(local.fairs, res.fairs, normalizeFair);
    const suppliers = mergeList(local.suppliers, res.suppliers, normalizeSupplier);

    await writeMerged({
      fairs: fairs.changed,
      suppliers: suppliers.changed,
      // The server is authoritative for settings only when it has a newer copy.
      settings: res.settings || null,
    });

    await outbox.settle([...send.map((r) => r.seq), ...drop]);

    const serverTime = res.serverTime || Date.now();
    await writeMeta(META.LAST_SYNC_AT, serverTime);

    const uploaded = await drainMedia(startedAs);

    await refreshPendingCount();
    setState({ status: 'idle', lastSyncAt: serverTime });

    return {
      pushed: send.length,
      pulledFairs: fairs.changed.length,
      pulledSuppliers: suppliers.changed.length,
      uploaded,
    };
  } catch (e) {
    const message = String((e && e.message) || e);
    setState({ status: 'error', error: message });
    await refreshPendingCount();
    return { error: message };
  }
}

/**
 * Upload pending media, one file per request, oldest first. Stops at the first
 * failure rather than hammering a dying connection — the next sync resumes
 * where this left off, which is the whole point of the pendingUpload index.
 */
async function drainMedia(startedAs) {
  const queue = (await media.pendingUploads()).sort((a, b) => a.createdAt - b.createdAt);
  let done = 0;

  for (const row of queue) {
    // One file per request leaves many gaps for a switch to land in, and a
    // booth photo uploaded under the wrong account is the same leak as a
    // supplier pushed under it.
    if (!sameIdentity(startedAs, currentIdentity())) break;

    if (!row.blob) {
      // Evicted before it ever uploaded — nothing to send, so stop tracking it.
      await media.markUploaded(row.id, null);
      continue;
    }
    try {
      const { path } = await transport.uploadMedia({
        mediaId: row.id,
        supplierId: row.supplierId,
        productId: row.productId,
        contactId: row.contactId,
        kind: row.kind,
        tag: row.tag,
        blob: row.blob,
        filename: filenameFor(row),
      });
      await media.markUploaded(row.id, path);
      await recordUploadedPath(row.supplierId, row.id, path);
      done += 1;
      setState({ pending: Math.max(0, state.pending - 1) });
    } catch (e) {
      break;
    }
  }
  return done;
}

/**
 * Write the server path back into the supplier's own media references.
 *
 * `media.reference()` snapshots `path` when the file is CAPTURED, which is
 * necessarily before it has been uploaded — so every reference starts life
 * with `path: null` and `markUploaded` only ever fixed up the media row, not
 * the supplier record that actually syncs.
 *
 * The consequence was invisible on the capturing phone, where photos resolve
 * from the local blob by `mediaId`, and total on anything else: a second
 * device (or this one after an eviction or a restore) received the supplier
 * with `path: null`, had no blob to fall back on, and showed nothing. For an
 * app whose whole point is that booth photos survive the trip, the server copy
 * has to be reachable from the record.
 *
 * Saving through the repo re-queues the supplier, so the path reaches the
 * server on the same sync.
 */
async function recordUploadedPath(supplierId, mediaId, path) {
  if (!supplierId || !mediaId || !path) return;
  try {
    const supplier = await getSupplier(supplierId);
    if (!supplier) return;

    let touched = false;
    const patch = (list) =>
      (list || []).map((ref) => {
        if (ref && (ref.mediaId === mediaId || ref.id === mediaId) && ref.path !== path) {
          touched = true;
          return { ...ref, path };
        }
        return ref;
      });

    const next = {
      ...supplier,
      photos: patch(supplier.photos),
      videos: patch(supplier.videos),
      products: (supplier.products || []).map((p) => ({ ...p, voiceNotes: patch(p.voiceNotes) })),
      contacts: (supplier.contacts || []).map((c) => ({ ...c, photos: patch(c.photos) })),
    };

    if (touched) await saveSupplier(next);
  } catch (e) {
    // The bytes are on the server either way; a failed back-fill just means
    // the reference is patched on a later upload rather than losing anything.
  }
}

function filenameFor(row) {
  const ext =
    (row.type && row.type.split('/')[1] && row.type.split('/')[1].split(';')[0]) ||
    (row.kind === 'photo' ? 'jpg' : row.kind === 'video' ? 'mp4' : 'm4a');
  return `${row.kind}-${row.id}.${ext}`;
}

/* ── Triggers ───────────────────────────────────────────────────────────── */

let wired = false;

/**
 * Sync when the browser says we are back online, and when the app regains
 * focus. Deliberately NOT on an interval: polling a dead network in a hall
 * drains the battery the buyer needs for the camera.
 */
export function startAutoSync() {
  if (wired || typeof window === 'undefined') return () => {};
  wired = true;

  const onOnline = () => syncNow();
  const onVisible = () => {
    if (document.visibilityState === 'visible') syncNow();
  };

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  syncNow();

  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    wired = false;
  };
}
